// src/types/marketing.ts - Types marketing avec support des produits et usageType et canal - MISE À JOUR businessId

import { createI18nLabelMap } from '../utils/i18n/getStatusLabel';

export type CampaignType = 
  | 'birthday'
  | 'holiday'
  | 'loyalty'
  | 'retention'
  | 'post_visit_followup'
  | 'cycle_reminder'
  | 'no_show_recovery'
  | 'cross_sell'
  | 'custom';

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed';
export type DiscountType = 'percentage' | 'fixed';
export type CampaignObjective =
  | 'reactivation'
  | 'repeat_booking'
  | 'upsell'
  | 'product_conversion'
  | 'loyalty_growth';

// Type d'usage des promotions
export type CampaignUsageType = 'single_use' | 'period_based';

// Canal de diffusion des promotions
export type CampaignChannel = 'whatsapp' | 'sms';

export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = createI18nLabelMap(
  'campaignType',
  [
    'birthday',
    'holiday',
    'loyalty',
    'retention',
    'post_visit_followup',
    'cycle_reminder',
    'no_show_recovery',
    'cross_sell',
    'custom',
  ] as const
);

export const CAMPAIGN_TYPE_COLORS: Record<CampaignType, string> = {
  birthday: '#ff6b9d',
  holiday: '#52c41a', 
  loyalty: '#faad14',
  retention: '#1890ff',
  post_visit_followup: '#13c2c2',
  cycle_reminder: '#2f54eb',
  no_show_recovery: '#f5222d',
  cross_sell: '#722ed1',
  custom: '#722ed1'
};

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = createI18nLabelMap(
  'campaignStatus',
  ['draft', 'active', 'paused', 'completed'] as const
);

export const CAMPAIGN_STATUS_COLORS: Record<CampaignStatus, string> = {
  draft: 'default',
  active: 'success',
  paused: 'warning', 
  completed: 'default'
};

// Labels et couleurs pour les types d'usage
export const CAMPAIGN_USAGE_TYPE_LABELS: Record<CampaignUsageType, string> = createI18nLabelMap(
  'campaignUsageType',
  ['single_use', 'period_based'] as const
);

export const CAMPAIGN_USAGE_TYPE_COLORS: Record<CampaignUsageType, string> = {
  single_use: 'orange',
  period_based: 'green'
};

export const CAMPAIGN_USAGE_TYPE_DESCRIPTIONS: Record<CampaignUsageType, string> = createI18nLabelMap(
  'campaignUsageTypeDesc',
  ['single_use', 'period_based'] as const
);

// Labels et couleurs pour les canaux de diffusion
export const CAMPAIGN_CHANNEL_LABELS: Record<CampaignChannel, string> = createI18nLabelMap(
  'campaignChannel',
  ['whatsapp', 'sms'] as const
);

export const CAMPAIGN_CHANNEL_COLORS: Record<CampaignChannel, string> = {
  whatsapp: 'green',
  sms: 'blue'
};

export const CAMPAIGN_CHANNEL_DESCRIPTIONS: Record<CampaignChannel, string> = createI18nLabelMap(
  'campaignChannelDesc',
  ['whatsapp', 'sms'] as const
);

// Estimation des coûts par canal (en FCFA par message)
export const CAMPAIGN_CHANNEL_COSTS: Record<CampaignChannel, number> = {
  whatsapp: 40,  // ~0,065 USD
  sms: 10        // SMS standard Cameroun
};

export interface OfferDetails {
  discountType: DiscountType;
  discountValue: number;
  endDate: Date;
  applicableServices?: string[];
  applicableProducts?: string[];
}

export interface TargetCriteria {
  birthdayPeriod?: { 
    startDate: Date;
    endDate: Date;
  };
  minLoyaltyBalance?: number;
  inactivityDays?: number;
  customClientIds?: string[];
}

export interface CampaignStats {
  messagesSent: number;
  messagesGenerated: number;
  conversionsCount: number;
  revenueGenerated: number;
}

export interface Campaign {
  id: string;
  businessId: string;        // ✅ MODIFIÉ : institutId → businessId
  name: string;
  description?: string;
  type: CampaignType;
  objective?: CampaignObjective;
  status: CampaignStatus;
  usageType: CampaignUsageType;
  channel: CampaignChannel;
  startDate: Date;
  targetCriteria: TargetCriteria;
  messageTemplate: string;
  offerDetails: OfferDetails;
  autoSend: boolean;
  stats: CampaignStats;
  createdAt: Date;
  createdBy: string;
  updatedAt?: Date;
  lastSentAt?: Date;
}

export interface ClientPromotion {
  id: string;
  businessId: string;       // ✅ MODIFIÉ : institutId → businessId
  campaignId: string;
  campaignName: string;
  channel?: CampaignChannel;
  clientId: string;
  discountType: DiscountType;
  discountValue: number;
  validUntil: Date;
  applicableServices?: string[];
  applicableProducts?: string[];
  messageText: string;
  clientName: string;
  clientPhone: string;
  active: boolean;
  used: boolean;
  sentAt?: Date;
  usedAt?: Date;
  createdAt: Date;
  prestationId?: string;
  
  // Champs pour la gestion des usages multiples
  usageCount: number;        // Nombre de fois utilisée
  lastUsedAt?: Date;         // Dernière utilisation
  maxUsages?: number;        // Limite d'utilisation (optionnel, pour évolutions futures)
  
  // Pour les campagnes SMS
  promoUrl?: string;         // URL de la landing page si campagne SMS
  promoHash?: string;        // Hash court utilise dans /p/{hash}
  code?: string;
}

export interface MarketingDashboardData {
  activeCampaigns: number;
  pendingMessages: number;
  totalConversions: number;
  totalRevenue: number;
  topPerformingCampaign?: {
    name: string;
    conversions: number;
    revenue: number;
  };
}

export interface CampaignFilters {
  status?: CampaignStatus;
  type?: CampaignType;
  objective?: CampaignObjective;
  usageType?: CampaignUsageType;
  channel?: CampaignChannel;
  dateRange?: [Date, Date];
  searchTerm?: string;
}

export const CAMPAIGN_OBJECTIVE_LABELS: Record<CampaignObjective, string> = createI18nLabelMap(
  'campaignObjective',
  ['reactivation', 'repeat_booking', 'upsell', 'product_conversion', 'loyalty_growth'] as const
);

export const CAMPAIGN_OBJECTIVE_DESCRIPTIONS: Record<CampaignObjective, string> = createI18nLabelMap(
  'campaignObjectiveDesc',
  ['reactivation', 'repeat_booking', 'upsell', 'product_conversion', 'loyalty_growth'] as const
);

export type ComputedCampaignStatus = 
  | 'scheduled'
  | 'active'
  | 'expired'
  | 'paused';

export const getComputedCampaignStatus = (campaign: Campaign): ComputedCampaignStatus => {
  const now = new Date();
  const startDate = new Date(campaign.startDate);
  const endDate = new Date(campaign.offerDetails.endDate);
  
  if (campaign.status === 'paused') {
    return 'paused';
  }
  
  if (now < startDate) {
    return 'scheduled';
  }
  
  if (now > endDate) {
    return 'expired';
  }
  
  return 'active';
};

export const COMPUTED_STATUS_LABELS: Record<ComputedCampaignStatus, string> = createI18nLabelMap(
  'computedCampaignStatus',
  ['scheduled', 'active', 'expired', 'paused'] as const
);

export const COMPUTED_STATUS_COLORS: Record<ComputedCampaignStatus, string> = {
  scheduled: 'processing',
  active: 'success',
  expired: 'default',
  paused: 'warning',
};

// Fonction pour déterminer les usageType recommandés par type de campagne
// L'utilisateur peut toujours modifier ce choix dans l'interface
export const getRecommendedUsageType = (campaignType: CampaignType): CampaignUsageType => {
  switch (campaignType) {
    case 'birthday':
      return 'single_use';     // Anniversaire = une seule fois
    case 'holiday':
      return 'period_based';   // Événements = pendant la période
    case 'loyalty':
      return 'single_use';     // Récompense fidélité = une fois
    case 'retention':
      return 'single_use';     // Retour client = une fois
    case 'post_visit_followup':
      return 'single_use';
    case 'cycle_reminder':
      return 'period_based';
    case 'no_show_recovery':
      return 'single_use';
    case 'cross_sell':
      return 'period_based';
    case 'custom':
      return 'period_based';   // Campagne personnalisée = flexible
    default:
      return 'single_use';
  }
};

// Fonction pour déterminer le canal recommandé par type de campagne
export const getRecommendedChannel = (campaignType: CampaignType): CampaignChannel => {
  switch (campaignType) {
    case 'birthday':
      return 'sms';       // SMS moins cher pour anniversaires
    case 'holiday':
      return 'sms';       // SMS pour événements ponctuels
    case 'loyalty':
      return 'whatsapp';  // WhatsApp pour récompenses (plus personnel)
    case 'retention':
      return 'whatsapp';  // WhatsApp pour reconquête (plus engageant)
    case 'post_visit_followup':
      return 'whatsapp';
    case 'cycle_reminder':
      return 'whatsapp';
    case 'no_show_recovery':
      return 'whatsapp';
    case 'cross_sell':
      return 'whatsapp';
    case 'custom':
      return 'sms';       // SMS par défaut pour campagnes custom
    default:
      return 'whatsapp';  // WhatsApp par défaut (rétrocompatibilité)
  }
};

export const getRecommendedTypeByObjective = (objective: CampaignObjective): CampaignType => {
  switch (objective) {
    case 'reactivation':
      return 'retention';
    case 'repeat_booking':
      return 'cycle_reminder';
    case 'upsell':
      return 'cross_sell';
    case 'product_conversion':
      return 'cross_sell';
    case 'loyalty_growth':
      return 'loyalty';
    default:
      return 'custom';
  }
};

// Fonction pour calculer le coût estimé d'une campagne
export const calculateCampaignCost = (
  clientCount: number, 
  channel: CampaignChannel = 'whatsapp'
): number => {
  return clientCount * CAMPAIGN_CHANNEL_COSTS[channel];
};